import React from 'react';
import { useBudget } from '../BudgetContext';
import {
  computeCumulativeSpendSeries,
  computeMonthlySpendByCategory,
  computeYearlyCategoryTotals,
  getAvailableYears,
  UNCATEGORIZED_FILTER,
  type CategoryInfo,
} from './budgetAnalytics';
import { SpendChart } from './SpendChart';
import { MonthlySpendChart } from './MonthlySpendChart';
import styles from './BudgetAnalyticsPanel.module.css';

type CategoryPickerValue = 'ALL_EXPENSES' | 'ALL_INCOME' | 'UNCATEGORIZED' | string;
type AccountPickerValue = 'ALL_ACCOUNTS' | string;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatSignedPercent(value: number): string {
  // value is percent units, e.g. 12.5 => "+13%"
  const rounded = Math.round(value);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '' : '';
  return `${sign}${rounded}%`;
}

function formatYtdWithPaceDelta(args: { currentYtd: number; previousYtd: number }): string {
  const { currentYtd, previousYtd } = args;
  if (previousYtd === 0) return formatCurrency(currentYtd);
  const percent = ((currentYtd - previousYtd) / previousYtd) * 100;
  return `${formatCurrency(currentYtd)} (${formatSignedPercent(percent)})`;
}

export const BudgetAnalyticsPanel: React.FC = () => {
  const {
    loading,
    error,
    transactions,
    categories,
    activeAccounts,
    applyFiltersFromAnalytics,
  } = useBudget();

  const expenseCategories = React.useMemo(() => {
    return categories
      .filter(c => c.type === 'expense')
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [categories]);

  const incomeCategories = React.useMemo(() => {
    return categories
      .filter(c => c.type === 'income')
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [categories]);

  const [categoryValue, setCategoryValue] = React.useState<CategoryPickerValue>('ALL_EXPENSES');
  const [accountValue, setAccountValue] = React.useState<AccountPickerValue>('ALL_ACCOUNTS');

  // Determine if we're viewing income categories
  const isIncomeView = React.useMemo(() => {
    if (categoryValue === 'ALL_INCOME') return true;
    const selectedCat = categories.find(c => c.id === categoryValue);
    return selectedCat?.type === 'income';
  }, [categoryValue, categories]);

  // Map the categoryValue to the actual filter values
  const selectedCategoryId = React.useMemo(() => {
    if (categoryValue === 'ALL_EXPENSES' || categoryValue === 'ALL_INCOME') return null;
    if (categoryValue === 'UNCATEGORIZED') return UNCATEGORIZED_FILTER;
    return categoryValue;
  }, [categoryValue]);
  
  const selectedAccountId = accountValue === 'ALL_ACCOUNTS' ? null : accountValue;
  const categoryType: 'expense' | 'income' = isIncomeView ? 'income' : 'expense';

  // Get available years from transactions
  const availableYears = React.useMemo(() => {
    return getAvailableYears(transactions);
  }, [transactions]);

  // Default to current year for monthly chart
  const [selectedYear, setSelectedYear] = React.useState<number>(() => {
    return availableYears.length > 0 ? availableYears[0] : new Date().getFullYear();
  });

  // Update selected year when available years change
  React.useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const analytics = React.useMemo(() => {
    return computeCumulativeSpendSeries(transactions, {
      categoryId: selectedCategoryId,
      accountId: selectedAccountId,
      categoryType,
    });
  }, [transactions, selectedCategoryId, selectedAccountId, categoryType]);

  // Convert categories to CategoryInfo format for monthly chart
  const categoryInfoList = React.useMemo((): CategoryInfo[] => {
    const sourceCategories = isIncomeView ? incomeCategories : expenseCategories;
    return sourceCategories.map(c => ({
      id: c.id,
      name: c.name,
      color: c.color || '#6B7280',
    }));
  }, [expenseCategories, incomeCategories, isIncomeView]);

  // Compute monthly spend data
  const monthlyData = React.useMemo(() => {
    return computeMonthlySpendByCategory(transactions, categoryInfoList, {
      year: selectedYear,
      categoryId: selectedCategoryId,
      accountId: selectedAccountId,
      categoryType,
    });
  }, [transactions, categoryInfoList, selectedYear, selectedCategoryId, selectedAccountId, categoryType]);

  // Compute yearly category totals for the summary table
  const yearlyCategoryData = React.useMemo(() => {
    return computeYearlyCategoryTotals(transactions, categories, {
      accountId: selectedAccountId,
    });
  }, [transactions, categories, selectedAccountId]);

  const applyMonthlySegmentFilters = React.useCallback(
    (args: { categoryId: string; monthIndex: number; year: number }) => {
      const { categoryId, monthIndex, year } = args;

      // Month start/end in UTC (YYYY-MM-DD)
      const monthStartUTC = new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0));
      const monthEndUTC = new Date(Date.UTC(year, monthIndex + 1, 0, 12, 0, 0));
      const dateFrom = monthStartUTC.toISOString().slice(0, 10);
      const dateTo = monthEndUTC.toISOString().slice(0, 10);

      // When viewing uncategorized in monthly chart, the categoryId is "uncategorized".
      // Transactions store uncategorized as null categoryId.
      const nextCategoryId = categoryId === 'uncategorized' ? null : categoryId;

      // Transactions: reset filters to match what the user clicked.
      // - Uncategorized: use quick filter (uncategorized is stored as null categoryId)
      // - Otherwise: use categoryId filter with quickFilter=all
      const quickFilter: 'all' | 'uncategorized' | 'expenses' | 'income' =
        categoryId === 'uncategorized' ? 'uncategorized' : 'all';

      const txCategoryId: string | null = categoryId === 'uncategorized' ? null : nextCategoryId;

      const nextFilters = {
        quickFilter,
        categoryId: txCategoryId,
        accountId: selectedAccountId,
        searchQuery: '',
        dateFrom,
        dateTo,
        sortField: 'date' as const,
        sortDirection: 'desc' as const,
      };

      const categoryName =
        categoryId === 'uncategorized'
          ? 'Uncategorized'
          : categories.find(c => c.id === categoryId)?.name || 'Category';

      applyFiltersFromAnalytics(nextFilters, {
        source: 'monthly-spend-chart',
        year,
        monthIndex,
        categoryId,
        categoryName,
      });

      // Auto-scroll to Transactions so the user immediately sees the effect.
      requestAnimationFrame(() => {
        const el = document.getElementById('budget-transactions');
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      // Keep analytics in sync with the click.
      if (categoryId === 'uncategorized') {
        setCategoryValue('UNCATEGORIZED');
      } else {
        setCategoryValue(categoryId);
      }
      setSelectedYear(year);
    },
    [applyFiltersFromAnalytics, categories, selectedAccountId]
  );

  const { points, currentYear, previousYear, twoYearsAgoYear, summary } = analytics;
  const hasAnySpend = points.length > 0 && (summary.currentTotal !== 0 || summary.previousTotal !== 0 || summary.twoYearsAgoTotal !== 0);
  const hasMonthlyData = monthlyData.points.some(p =>
    monthlyData.categories.some(cat => (p[cat.id] as number) > 0)
  );

  return (
    <section className={styles.panel} aria-label="Budget analytics">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Analytics</h2>
          <p className={styles.subtitle}>
            Cumulative {isIncomeView ? 'income' : 'spend'} (calendar year to date) vs prior year
          </p>
        </div>
      </div>

      {error && <div className={styles.empty}>Failed to load budget data: {error}</div>}
      {loading && <div className={styles.empty}>Loading analytics…</div>}

      {!loading && !error && (
        <>
          <div className={styles.grid}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>Cumulative {isIncomeView ? 'Income' : 'Spend'} (YTD)</h3>
                  <p className={styles.cardSubtitle}>Calendar year-to-date vs prior year</p>
                </div>
                <div className={styles.inlineControls}>
                  <div className={styles.control}>
                    <span className={styles.label}>Category</span>
                    <select
                      className={styles.select}
                      value={categoryValue}
                      onChange={e => setCategoryValue(e.target.value as CategoryPickerValue)}
                    >
                      <optgroup label="Expenses">
                        <option value="ALL_EXPENSES">All expenses</option>
                        <option value="UNCATEGORIZED">Uncategorized</option>
                        {expenseCategories.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Income">
                        <option value="ALL_INCOME">All income</option>
                        {incomeCategories.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div className={styles.control}>
                    <span className={styles.label}>Account</span>
                    <select
                      className={styles.select}
                      value={accountValue}
                      onChange={e => setAccountValue(e.target.value as AccountPickerValue)}
                    >
                      <option value="ALL_ACCOUNTS">All accounts</option>
                      {activeAccounts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {hasAnySpend ? (
                <SpendChart
                  points={points}
                  currentYearLabel={`${currentYear} YTD`}
                  previousYearLabel={`${previousYear} YTD`}
                  twoYearsAgoLabel={`${twoYearsAgoYear} YTD`}
                />
              ) : (
                <div className={styles.empty}>
                  No {isIncomeView ? 'income' : 'expense'} transactions found for this year-to-date window.
                </div>
              )}
            </div>

            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.cardTitle}>Monthly {isIncomeView ? 'Income' : 'Spend'} by Category</h3>
                  <p className={styles.cardSubtitle}>Stacked breakdown</p>
                </div>
                <div className={styles.inlineControls}>
                  <div className={styles.control}>
                    <span className={styles.label}>Year</span>
                    <select
                      className={styles.select}
                      value={selectedYear}
                      onChange={e => setSelectedYear(Number(e.target.value))}
                    >
                      {availableYears.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {hasMonthlyData ? (
                <MonthlySpendChart data={monthlyData} onSegmentClick={applyMonthlySegmentFilters} />
              ) : (
                <div className={styles.empty}>
                  No {isIncomeView ? 'income' : 'expense'} transactions found for {selectedYear}.
                </div>
              )}
            </div>

            <div className={styles.tableCard} aria-label="Category yearly totals">
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>Category Totals</h3>
                <p className={styles.tableSubtitle}>Two years ago, prior year, and current YTD</p>
              </div>
              <div className={styles.tableScroll}>
                <table>
                  <thead>
                    <tr>
                      <th className={styles.categoryHeader}>Category</th>
                      <th className={styles.amountHeader}>{yearlyCategoryData.twoYearsAgoYear}</th>
                      <th className={styles.amountHeader}>{yearlyCategoryData.previousYear}</th>
                      <th className={styles.amountHeader}>{yearlyCategoryData.currentYear} YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyCategoryData.rows.map(row => (
                      <tr key={row.categoryId} className={row.categoryType === 'income' ? styles.incomeRow : undefined}>
                        <td className={styles.categoryCell}>
                          <span className={styles.categoryDot} style={{ backgroundColor: row.color }} />
                          {row.categoryName}
                        </td>
                        <td className={styles.amountCell}>{formatCurrency(row.twoYearsAgo)}</td>
                        <td className={styles.amountCell}>{formatCurrency(row.previousYear)}</td>
                        <td className={styles.amountCell}>
                          {formatYtdWithPaceDelta({ currentYtd: row.currentYearYtd, previousYtd: row.previousYearYtd })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={styles.totalRow}>
                      <td className={styles.categoryCell}>Total</td>
                      <td className={styles.amountCell}>{formatCurrency(yearlyCategoryData.totals.twoYearsAgo)}</td>
                      <td className={styles.amountCell}>{formatCurrency(yearlyCategoryData.totals.previousYear)}</td>
                      <td className={styles.amountCell}>
                        {formatYtdWithPaceDelta({
                          currentYtd: yearlyCategoryData.totals.currentYearYtd,
                          previousYtd: yearlyCategoryData.totals.previousYearYtd,
                        })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
